import React, { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Rocket, Radar, Mail, Megaphone, Loader2, Copy, ExternalLink, CheckCircle2, XCircle, Shield,
} from "lucide-react";

const copy = (text) => {
  navigator.clipboard.writeText(text);
  toast.success("Copied to clipboard");
};

export default function GrowthPage() {
  const { user } = useAuth();
  const [pages, setPages] = useState([]);
  const [forbidden, setForbidden] = useState(false);

  // IndexNow
  const [pinging, setPinging] = useState(false);
  const [pingResult, setPingResult] = useState(null);

  // Outreach
  const [niche, setNiche] = useState("");
  const [outLoading, setOutLoading] = useState(false);
  const [outreach, setOutreach] = useState(null);

  // Directory
  const [directory, setDirectory] = useState("Product Hunt");
  const [dirLoading, setDirLoading] = useState(false);
  const [dirCopy, setDirCopy] = useState(null);

  useEffect(() => {
    api.get("/seo/pages").then((r) => setPages(r.data)).catch((e) => {
      if (e?.response?.status === 403) setForbidden(true);
    });
  }, []);

  const forPages = pages.filter((p) => p.page_type === "for");
  const vsPages = pages.filter((p) => p.page_type === "vs");

  if (forbidden || (user && user.is_admin === false)) {
    return (
      <div className="py-24 text-center" data-testid="growth-forbidden">
        <Shield className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-50" />
        <h2 className="text-xl font-display font-bold">Admin only</h2>
        <p className="text-muted-foreground mt-1">This area is restricted to AutoGridSocial administrators.</p>
      </div>
    );
  }

  const pingIndexNow = async () => {
    setPinging(true); setPingResult(null);
    try {
      const r = await api.post("/admin/growth/ping_indexnow", { origin_url: window.location.origin });
      setPingResult(r.data);
      toast[r.data.ok ? "success" : "error"](r.data.ok ? "Submitted to IndexNow" : "IndexNow returned an error");
    } catch (e) {
      toast.error("IndexNow ping failed");
    } finally { setPinging(false); }
  };

  const genOutreach = async () => {
    if (!niche) { toast.error("Pick a niche first"); return; }
    setOutLoading(true); setOutreach(null);
    try {
      const r = await api.post("/admin/growth/outreach", { niche_slug: niche });
      setOutreach(r.data);
    } catch (e) { toast.error("Could not generate email"); }
    finally { setOutLoading(false); }
  };

  const genDirectory = async () => {
    setDirLoading(true); setDirCopy(null);
    try {
      const r = await api.post("/admin/growth/directory-copy", { directory });
      setDirCopy(r.data.copy);
    } catch (e) { toast.error("Could not generate copy"); }
    finally { setDirLoading(false); }
  };

  return (
    <div className="space-y-8" data-testid="growth-page">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-accent/20 flex items-center justify-center">
          <Rocket className="h-5 w-5 text-accent" />
        </div>
        <div>
          <h1 className="text-3xl font-display font-bold tracking-tight">Growth Panel</h1>
          <p className="text-muted-foreground mt-1">Programmatic SEO, cold outreach & directory launches. Admin only.</p>
        </div>
      </div>

      {/* IndexNow */}
      <div className="rounded-xl border border-border bg-card p-6" data-testid="growth-indexnow">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <Radar className="h-5 w-5 text-primary mt-0.5" />
            <div>
              <h3 className="font-semibold">Ping search engines (IndexNow)</h3>
              <p className="text-xs text-muted-foreground mt-1">
                Submit all {pages.length} pages to Bing/Yandex instantly. Requires the key file hosted at your domain root.
              </p>
            </div>
          </div>
          <Button data-testid="growth-ping-button" onClick={pingIndexNow} disabled={pinging}
            className="bg-primary hover:bg-primary/90 text-white">
            {pinging ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Radar className="h-4 w-4 mr-2" />}
            Ping IndexNow
          </Button>
        </div>
        {pingResult && (
          <div className="mt-4 rounded-lg border border-border bg-secondary/30 p-4 text-sm space-y-2" data-testid="growth-ping-result">
            <div className="flex items-center gap-2">
              {pingResult.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <XCircle className="h-4 w-4 text-rose-400" />}
              <span>HTTP {pingResult.status_code} · {pingResult.submitted} URLs submitted</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Host a key file at: <code className="text-primary break-all">{pingResult.key_location}</code>
              <br />containing the key: <code className="text-primary">{pingResult.key}</code>
            </p>
            {pingResult.note && <p className="text-xs text-muted-foreground">Response: {pingResult.note}</p>}
          </div>
        )}
      </div>

      {/* Niche outreach */}
      <div className="rounded-xl border border-border bg-card p-6" data-testid="growth-outreach">
        <div className="flex items-start gap-3 mb-4">
          <Mail className="h-5 w-5 text-primary mt-0.5" />
          <div>
            <h3 className="font-semibold">Niche cold outreach email</h3>
            <p className="text-xs text-muted-foreground mt-1">Generate a human, anti-slop cold email for a target niche.</p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={niche} onValueChange={setNiche}>
            <SelectTrigger className="w-64" data-testid="growth-niche-select">
              <SelectValue placeholder="Choose a niche" />
            </SelectTrigger>
            <SelectContent>
              {forPages.map((p) => (
                <SelectItem key={p.slug} value={p.slug} data-testid={`growth-niche-${p.slug}`}>{p.audience}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button data-testid="growth-outreach-button" onClick={genOutreach} disabled={outLoading}
            className="bg-primary hover:bg-primary/90 text-white">
            {outLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
            Generate email
          </Button>
        </div>
        {outreach && (
          <div className="mt-4 rounded-lg border border-border bg-secondary/30 p-4" data-testid="growth-outreach-result">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-mono text-muted-foreground uppercase">Subject</span>
              <Button size="sm" variant="ghost" className="h-7 text-xs"
                onClick={() => copy(`${outreach.email.subject}\n\n${outreach.email.body}`)}>
                <Copy className="h-3 w-3 mr-1" /> Copy all
              </Button>
            </div>
            <p className="font-medium text-sm mb-3">{outreach.email.subject}</p>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">{outreach.email.body}</p>
          </div>
        )}
      </div>

      {/* Directory launch copy */}
      <div className="rounded-xl border border-border bg-card p-6" data-testid="growth-directory">
        <div className="flex items-start gap-3 mb-4">
          <Megaphone className="h-5 w-5 text-primary mt-0.5" />
          <div>
            <h3 className="font-semibold">Directory launch copy kit</h3>
            <p className="text-xs text-muted-foreground mt-1">Tagline, descriptions, tags & first comment for a launch directory.</p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Input data-testid="growth-directory-input" value={directory} onChange={(e) => setDirectory(e.target.value)}
            placeholder="Product Hunt" className="w-64" />
          <Button data-testid="growth-directory-button" onClick={genDirectory} disabled={dirLoading}
            className="bg-primary hover:bg-primary/90 text-white">
            {dirLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Megaphone className="h-4 w-4 mr-2" />}
            Generate kit
          </Button>
        </div>
        {dirCopy && (
          <div className="mt-4 space-y-3" data-testid="growth-directory-result">
            {[
              ["Tagline", dirCopy.tagline],
              ["Short description", dirCopy.short_description],
              ["Long description", dirCopy.long_description],
              ["First comment", dirCopy.first_comment],
            ].map(([label, val]) => val && (
              <div key={label} className="rounded-lg border border-border bg-secondary/30 p-4">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-mono text-muted-foreground uppercase">{label}</span>
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => copy(val)}>
                    <Copy className="h-3 w-3 mr-1" /> Copy
                  </Button>
                </div>
                <p className="text-sm text-foreground/90 whitespace-pre-wrap leading-relaxed">{val}</p>
              </div>
            ))}
            {dirCopy.tags && (
              <div className="flex flex-wrap gap-2">
                {dirCopy.tags.map((t) => <Badge key={t} className="bg-primary/15 text-primary border-primary/30">{t}</Badge>)}
              </div>
            )}
          </div>
        )}
      </div>

      {/* SEO pages index */}
      <div className="rounded-xl border border-border bg-card p-6" data-testid="growth-seo-pages">
        <h3 className="font-semibold mb-4">Live SEO pages ({pages.length})</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {[...forPages, ...vsPages].map((p) => (
            <a key={`${p.page_type}-${p.slug}`} href={`/${p.page_type}/${p.slug}`} target="_blank" rel="noreferrer"
              className="flex items-center justify-between rounded-lg border border-border bg-secondary/20 px-3 py-2 text-sm hover:border-primary/40 transition-colors"
              data-testid={`growth-seo-link-${p.page_type}-${p.slug}`}>
              <span className="truncate">/{p.page_type}/{p.slug}</span>
              <ExternalLink className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            </a>
          ))}
        </div>
        <a href={`${API}/sitemap.xml`} target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-xs text-primary mt-4 hover:underline" data-testid="growth-sitemap-link">
          View sitemap.xml <ExternalLink className="h-3 w-3" />
        </a>
      </div>
    </div>
  );
}
